import { Modal } from '@/components/ui/Modal';
import { Skeleton } from '@/components/ui/Skeleton';
import { TrendDelta } from '@/components/ui/TrendDelta';
import { useDomainSubdomainTrend } from '@/hooks/api/useDashboard';
import { scoreBucket, type DomainMeta } from '@/data/mockPerformance';
import type { DashboardFilters, DomainSubdomain } from '@/api/dashboard';
import { visibleChartSections } from './chartSections';
import styles from './DomainTrendModal.module.css';

interface DomainTrendModalProps {
  open: boolean;
  onClose: () => void;
  domain: DomainMeta;
  /**
   * Filters from the Lens page (dateRange + optional start/end). Threaded
   * through to the sub-domain-trend route so the modal's monthly bars and
   * N= reflect the same window the Lens page does, instead of a hardcoded
   * 4-month default.
   */
  filters?: DashboardFilters;
}

/**
 * Sub-domain trend drill-down.
 *
 * Opened from the Trends button on Lens (PLA-1020); it previously lived
 * behind the Home RSQ tile. Renders the provider's overall score,
 * tenant rank, and chart count in the header, then one row per sub-domain
 * (History (HPI) / Physical Exam / MDM / Vital Signs / Labs+Imaging) with
 * its current score, ordinal rank tag, 4-month sparkline, and a goal-marker
 * progress bar -- matching `#domTrendModal` in `Kai-API/Mock/Kai-Mock-V2.html`.
 *
 * Data: `/domains/:domain/subdomain-trend`, computed from
 * `gold_indicator_analytics` with the same filters and RSQ formula as the
 * Home tile (grouped by `chart_section`, bucketed by month), so the header
 * score is the tile score and the rows are its section breakdown. Only
 * RSQ is wired today; other domains fall back to the empty-data placeholder.
 * Per-sub-domain rank is tracked in PLA-479 (not pre-computed in gold yet).
 */
export function DomainTrendModal({ open, onClose, domain, filters }: DomainTrendModalProps) {
  const enabled = open;
  const query = useDomainSubdomainTrend(enabled ? domain.key : undefined, filters);
  const data = query.data;
  // Vitals is hidden from the section surfaces (K4); see chartSections.ts.
  const subs = data ? visibleChartSections(data.subs) : [];

  const title = (
    <h2 id="domain-trend-title" className={styles.title}>
      {/* Matches the Lens call-out this opens from (V&V K3). The API label
          ("RSQ® Indicators") described the old Home drill-down. */}
      {domain.key === 'rsq' ? 'Chart Section Trends' : `${domain.label} Trends`}
    </h2>
  );

  // "54% overall · 14th/20 · N=175 · Mar – Jun 2026" -- only show pieces
  // we actually have. windowLabel makes the trend window explicit so the
  // user can verify what time range the score / sparkline / N reflect.
  const subtitle = (() => {
    if (!data) return null;
    const parts: string[] = [];
    if (data.score != null) parts.push(`${data.score}% overall`);
    if (data.rank != null && data.cohortSize != null) {
      parts.push(`${ordinal(data.rank)}/${data.cohortSize}`);
    }
    if (data.chartCount != null) parts.push(`N=${data.chartCount.toLocaleString()}`);
    if (data.windowLabel) parts.push(data.windowLabel);
    return parts.length ? parts.join(' · ') : null;
  })();

  return (
    <Modal
      open={open}
      onClose={onClose}
      labelledBy="domain-trend-title"
      fullScreenOnMobile
      title={title}
      subtitle={subtitle ?? undefined}
    >
      {domain.key !== 'rsq' ? (
        <div className={styles.notWired}>
          <strong>{domain.fullName} trends aren't connected yet.</strong>
          <p>Kai-API only exposes RSQ®-scoped sub-domain trends today.</p>
        </div>
      ) : query.isLoading ? (
        <ul className={styles.list} aria-busy="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <li key={i} className={styles.row}>
              <Skeleton variant="text" width="50%" />
              <Skeleton variant="text" width={40} />
            </li>
          ))}
        </ul>
      ) : query.isError ? (
        <div className={styles.notWired}>
          <strong>Couldn't load sub-domain trends.</strong>
          <p>{(query.error as Error)?.message ?? 'Kai-API request failed.'}</p>
        </div>
      ) : !data || subs.length === 0 ? (
        <div className={styles.notWired}>
          <strong>No sub-domain data for this window.</strong>
          <p>{data?.reason ?? 'Gold-layer rows are missing for the active filters.'}</p>
        </div>
      ) : (
        <ul className={styles.list}>
          {subs.map((sub) => (
            <SubdomainRow key={sub.id} sub={sub} />
          ))}
        </ul>
      )}
    </Modal>
  );
}

interface SubdomainRowProps {
  sub: DomainSubdomain;
}

function SubdomainRow({ sub }: SubdomainRowProps) {
  const score = sub.score;
  const bucket = scoreBucket(score);
  // Trend = period-over-period: this window's section score minus the equal
  // prior window's, computed by the API with the same formula as the RSQ
  // tile and the Lens row chips. An explicit null means no prior data. Only
  // a legacy payload with no `delta` key falls back to the sparkline diff.
  const points = sub.history.filter((v): v is number => v != null);
  const delta =
    'delta' in sub
      ? (sub.delta ?? null)
      : points.length >= 2
        ? Math.round(points[points.length - 1] - points[0])
        : null;
  // Same +/-2pt "flat" band as every other trend chip in the app.
  const dir: 'up' | 'down' | 'stable' =
    delta == null ? 'stable' : delta >= 2 ? 'up' : delta <= -2 ? 'down' : 'stable';
  const deltaTitle =
    delta == null
      ? 'No prior period to compare against'
      : `${dir === 'up' ? 'Up' : dir === 'down' ? 'Down' : 'Steady,'} ${Math.abs(delta)} points vs the prior period${
          sub.previousScore != null ? ` (was ${sub.previousScore}%)` : ''
        }`;

  return (
    <li className={styles.row}>
      <div className={styles.rowMain}>
        <span
          className={`${styles.colorBar} ${styles[`fill_${bucket}`]}`}
          aria-hidden="true"
        />
        <div className={styles.nameCol}>
          <span className={styles.name}>{sub.name}</span>
          {/* Per-sub-domain rank isn't pre-computed in gold yet (PLA-479).
              We omit the rank meta-line until that lands. */}
        </div>
        <Sparkline scores={sub.history} bucket={bucket} />
        <div className={styles.scoreCol}>
          <span className={`${styles.score} ${styles[`score_${bucket}`]}`}>
            {score != null ? `${score}%` : '--'}
          </span>
          {/* Points, not percent: a 60% -> 67% move is "7 pts" with an up arrow. */}
          <TrendDelta
            className={styles.deltaPill}
            delta={delta}
            direction={dir === 'stable' ? 'flat' : dir}
            unit="pts"
            size="sm"
            title={deltaTitle}
          />
        </div>
      </div>
      <div className={styles.progressTrack} aria-hidden="true">
        <span className={styles.progressGoalLabel} style={{ left: '80%' }}>
          80%
        </span>
        <span className={styles.progressGoalMarker} style={{ left: '80%' }} />
        <div
          className={`${styles.progressFill} ${styles[`fill_${bucket}`]}`}
          style={{ width: `${score ?? 0}%` }}
        />
      </div>
    </li>
  );
}

/**
 * 4-point sparkline with end-of-series dot, matching the mock's `makeSparkline`
 * (64x28 SVG, colored by current score bucket). Null points are skipped; if
 * fewer than 2 points have data, renders a muted placeholder dash.
 */
function Sparkline({
  scores,
  bucket,
}: {
  scores: (number | null)[];
  bucket: ReturnType<typeof scoreBucket>;
}) {
  const defined = scores
    .map((s, i) => ({ s, i }))
    .filter((p): p is { s: number; i: number } => p.s != null);
  if (defined.length < 2) {
    return <span className={styles.sparkPlaceholder} aria-hidden="true">—</span>;
  }
  const width = 64;
  const height = 28;
  const padX = 2;
  const padY = 2;
  const min = Math.min(...defined.map((p) => p.s)) - 5;
  const max = Math.max(...defined.map((p) => p.s)) + 5;
  const range = max - min || 1;
  const project = (p: { s: number; i: number }) => {
    const x = padX + (p.i / Math.max(1, scores.length - 1)) * (width - padX * 2);
    const y = height - padY - ((p.s - min) / range) * (height - padY * 2);
    return [x, y] as const;
  };
  const points = defined.map(project).map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const [lastX, lastY] = project(defined[defined.length - 1]);
  return (
    <svg
      className={`${styles.spark} ${styles[`fill_${bucket}`]}`}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lastX} cy={lastY} r="3" fill="currentColor" />
    </svg>
  );
}

/** "14" -> "14th", "1" -> "1st", "2" -> "2nd", "3" -> "3rd". */
function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}
